import React, { useState } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";
import { useGetGameFileList } from "hooks/useGetGameFileList";
import { useChunkedUpload } from "hooks/useChunkedUpload";
import Dropzone from "react-dropzone";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import { ResourceType } from "models/ResourceType";

const SelectRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const StyledSelect = styled(Select)({
    flex: 1,
    "& fieldset": {
        borderRadius: "19px",
    },
});

const DropzoneBox = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
    },
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
    marginRight: theme.spacing(2),
}));

const StyledDialogCloseButton = styled(IconButton)(({ theme }) => ({
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
}));

interface GameFileSelectorProps {
    value: string | null;
    onChange: (fileId: string | null) => void;
}

export const GameFileSelector = ({ value, onChange }: GameFileSelectorProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { files, gameFilesLoading, refetch: refetchGameFiles } = useGetGameFileList();
    const { upload } = useChunkedUpload();

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleSelectChange = (event: { target: { value: unknown } }): void => {
        const newValue = String(event.target.value ?? "");
        onChange(newValue === "" ? null : newValue);
    };

    const handleUploadClick = () => {
        setUploadDialogOpen(true);
    };

    const handleFilesDrop = async (acceptedFiles: File[]) => {
        if (uploading || acceptedFiles.length === 0) return;
        setUploading(true);

        const file = acceptedFiles[0];
        const result = await upload(file, ResourceType.Game, null);

        if (result.success && result.data) {
            onChange(result.data.fileId);
            await refetchGameFiles();
            setUploadDialogOpen(false);
            enqueueSnackbar(t("gameEditor.fileUploadSuccess"), { variant: "success" });
        } else if (result.errors?.length) {
            result.errors.forEach((error) => {
                enqueueSnackbar(t(`api.${error.code}`), { variant: "error" });
            });
        }

        setUploading(false);
    };

    return (
        <>
            <SelectRow>
                <FormControl fullWidth size="small">
                    <InputLabel id="game-file-select-label">
                        {t("gameEditor.selectFile")}
                    </InputLabel>
                    <StyledSelect
                        labelId="game-file-select-label"
                        id="game-file-select"
                        value={value ?? ""}
                        label={t("gameEditor.selectFile")}
                        onChange={(e) => handleSelectChange(e)}
                        disabled={gameFilesLoading}
                        endAdornment={
                            gameFilesLoading ? (
                                <StyledCircularProgress size={20} />
                            ) : undefined
                        }
                    >
                        <MenuItem value="">
                            <em>{t("gameEditor.noFileSelected")}</em>
                        </MenuItem>
                        {files.map((file) => (
                            <MenuItem key={file.id} value={file.id}>
                                {file.fileName}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </FormControl>
                <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={handleUploadClick}
                    disabled={gameFilesLoading}
                >
                    {t("gameEditor.upload")}
                </Button>
            </SelectRow>

            <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)}>
                <DialogTitle>{t("gameEditor.uploadFile")}</DialogTitle>
                <StyledDialogCloseButton
                    aria-label="close"
                    onClick={() => !uploading && setUploadDialogOpen(false)}
                >
                    <CloseIcon />
                </StyledDialogCloseButton>
                <DialogContent>
                    <Dropzone
                        onDrop={handleFilesDrop}
                        accept="application/zip,.zip,application/vnd.rar,.rar,application/x-rar-compressed,.aab,application/vnd.android.package-archive,.apk"
                        maxFiles={1}
                        disabled={uploading}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <DropzoneBox {...getRootProps()}>
                                <input {...getInputProps()} />
                                {uploading ? (
                                    <CircularProgress />
                                ) : (
                                    <>
                                        <p>{t("gameEditor.dragAndDrop")}</p>
                                        <em>{t("gameEditor.acceptedFormats")}</em>
                                    </>
                                )}
                            </DropzoneBox>
                        )}
                    </Dropzone>
                </DialogContent>
            </Dialog>
        </>
    );
};
